import { hole } from './api'

/** Der Mensch am Vorgang (§8.1). */
export interface Mensch {
  firebase_uid: string
  username: string | null
  display_name: string | null
  is_anonymous: boolean
  points: number
  created_at: string
  profilbild: string | null
  /** 400er-Variante; kann fehlen, dann greift das Original. */
  profilbild_klein: string | null
  bewertungen: number
  fotos: number
  abgelehnt: number
  /** `null` = kein Gerät bekannt; dann steht nichts da. */
  push: boolean | null
}

export interface Punktebewegung {
  vorher: number | null
  nachher: number | null
  differenz: number | null
}

/** Was eine Aktion bewirkt hat — die Oberfläche zeigt es danach an (§10). */
export interface Ergebnis {
  punkte: Punktebewegung
  [weiteres: string]: unknown
}

// --- Übersicht -----------------------------------------------------------

export interface Schlange {
  name: 'fotos' | 'bewertungen' | 'einreichungen' | 'meldungen' | 'profilmeldungen'
  anzahl: number
  aeltestes: string | null
}

export interface LetzteEntscheidung {
  target_kind: string
  target_id: string
  domain: string | null
  decision: string
  reason: string | null
  decided_at: string
  betroffener: string | null
  betroffener_name: string | null
  entscheider_name: string | null
}

export interface Uebersicht {
  schlangen: Schlange[]
  letzte: LetzteEntscheidung[]
}

export const holeUebersicht = () => hole<Uebersicht>('/api/uebersicht')

// --- Fotos ---------------------------------------------------------------

/**
 * Was ein Bildmodell auf dem Foto erkannt hat (Migration 080).
 *
 * `laeuft` heißt: Das Modell ist noch dabei. Die Entscheidung über das Foto
 * hängt nie daran — Freigeben und Ablehnen gehen immer.
 */
export interface ErkannterTreffer {
  schluessel: string
  bezeichnung: string
  /** 0 bis 1. */
  sicherheit: number
  beleg: string
}

export interface Erkennung {
  zustand: 'ok' | 'fehler' | 'laeuft'
  modell: string | null
  erkannt: ErkannterTreffer[]
  fehler: string | null
}

/**
 * Der Merkmalskatalog, wie ihn die API liefert. **Die Oberfläche hält keine
 * eigene Liste** — sie stammt aus `Merkmale.swift` der App und würde sonst
 * auseinanderlaufen.
 */
export interface Merkmal {
  schluessel: string
  bezeichnung: string
  gruppe: string
  /** In welche Spalte es geht: Ausstattung oder Umfeld. */
  speicher: 'features' | 'accessibility'
}

export const holeMerkmale = () => hole<{ merkmale: Merkmal[] }>('/api/merkmale')

/** Ein Bild mit seinem Ergebnis. Der Schlüssel ist der Pfad (Migration 081). */
export interface BildErkennung {
  pfad: string
  erkennung: Erkennung
}

export interface Foto {
  id: string
  storage_path: string
  variants: Record<string, string> | null
  uploaded_at: string | null
  created_at: string
  beitragender: string | null
  place_id: string | null
  ort_name: string | null
  ort_strasse: string | null
  ort_plz: string | null
  ort_stadt: string | null
  bilder_am_ort: number
  /** Die Ausstattung des Ortes, wie sie jetzt dasteht. */
  ort_features: Record<string, unknown> | null
  /** Das Umfeld des Ortes — Bänke, Toiletten, Zaun. */
  ort_accessibility: Record<string, unknown> | null
  erkennung: Erkennung
  mensch: Mensch | null
}

/** Die Liste ist gedeckelt; `gesamt` ist die wahre Zahl der Wartenden. */
export interface Liste<T> {
  eintraege: T[]
  gesamt: number
}

export const holeFotos = () => hole<Liste<Foto>>('/api/fotos')

export const fotoFreigeben = (id: string, merkmale: string[] = []) =>
  hole<Ergebnis>(`/api/fotos/${id}/freigeben`, {
    method: 'POST',
    body: JSON.stringify({ merkmale }),
  })

export const fotoAblehnen = (
  id: string,
  grundCode: Ablehnungsgrund,
  merkmale: string[] = [],
) =>
  hole<Ergebnis>(`/api/fotos/${id}/ablehnen`, {
    method: 'POST',
    body: JSON.stringify({ grundCode, merkmale }),
  })

export const holeErkennung = (id: string) =>
  hole<Erkennung>(`/api/fotos/${id}/erkennung`)

export const erkennungErneut = (id: string) =>
  hole<{ angestossen: boolean }>(`/api/fotos/${id}/erkennung`, { method: 'POST' })

export const holeEinreichungErkennung = (id: string) =>
  hole<{ bilder: BildErkennung[] }>(`/api/einreichungen/${id}/erkennung`)

export const einreichungErkennungErneut = (id: string) =>
  hole<{ angestossen: boolean }>(`/api/einreichungen/${id}/erkennung`, { method: 'POST' })

// --- Bewertungstexte -----------------------------------------------------

export interface Bewertung {
  place_id: string
  user_firebase_uid: string
  rating: number
  title: string | null
  text: string | null
  status: string
  text_status: string
  submitted_at: string
  ort_name: string | null
  ort_strasse: string | null
  ort_stadt: string | null
  mensch: Mensch | null
}

export const holeBewertungen = () => hole<Liste<Bewertung>>('/api/bewertungen')

export const textFreigeben = (b: Bewertung) =>
  hole<Ergebnis>(
    `/api/bewertungen/${b.place_id}/${encodeURIComponent(b.user_firebase_uid)}/freigeben`,
    { method: 'POST' },
  )

export const textAblehnen = (b: Bewertung, grundCode: Ablehnungsgrund) =>
  hole<Ergebnis>(
    `/api/bewertungen/${b.place_id}/${encodeURIComponent(b.user_firebase_uid)}/ablehnen`,
    { method: 'POST', body: JSON.stringify({ grundCode }) },
  )

// --- Einreichungen -------------------------------------------------------

export interface Vorschau {
  features: Record<string, unknown>
  accessibility: Record<string, unknown>
  ageMinMonths: number | null
  ageMaxMonths: number | null
  /** Vorgeschlagener Ortsname, getrimmt. Nur bei `place_edit` anzuwenden. */
  name: string | null
  /** Freitext an die Moderation — geht in keine Spalte. */
  hinweis: string | null
  /**
   * Gruppen, die der Bildschirm gezeigt hat. Nachweis, dass gefragt wurde;
   * **keine** Aussage darüber, dass fehlende Merkmale „nein" bedeuten.
   */
  gesehen: string[]
  unbekannt: Record<string, unknown>
}

export interface Einreichung {
  id: string
  kind: 'info' | 'place_edit' | 'place_new' | 'photo' | 'review'
  user_firebase_uid: string
  place_id: string | null
  payload: {
    werte?: Record<string, unknown>
    gesehen?: string[]
    quelle?: string
  } | null
  submitted_at: string
  ort_name: string | null
  ort_strasse: string | null
  ort_plz: string | null
  ort_stadt: string | null
  ort_features: Record<string, unknown> | null
  ort_accessibility: Record<string, unknown> | null
  ort_age_min_months: number | null
  ort_age_max_months: number | null
  ort_lat: number | null
  ort_lon: number | null
  /** `osm` | `editorial` | `user` — woher der heutige Name stammt. */
  ort_herkunft: string | null
  /** Bei `place_new`: die eingereichten Bilder mit dem, was das Modell sieht. */
  bilder: BildErkennung[]
  vorschau: Vorschau
  mensch: Mensch | null
}

export interface Nachbar {
  id: string
  name: string | null
  street: string | null
  city: string | null
  status: string
  meter: number
}

export const holeEinreichungen = () => hole<Liste<Einreichung>>('/api/einreichungen')

export const holeNachbarn = (lat: number, lon: number, ausser?: string | null) =>
  hole<{ nachbarn: Nachbar[] }>(
    `/api/nachbarn?lat=${encodeURIComponent(lat)}&lon=${encodeURIComponent(lon)}` +
      (ausser ? `&ausser=${encodeURIComponent(ausser)}` : ''),
  ).then((a) => a.nachbarn)

export const einreichungAnnehmen = (id: string, merkmale: string[] = []) =>
  hole<Ergebnis & { result_place_id: string | null; bilder_angelegt: number | null }>(
    `/api/einreichungen/${id}/annehmen`,
    { method: 'POST', body: JSON.stringify({ merkmale }) },
  )

export const einreichungAblehnen = (id: string, grundCode: Ablehnungsgrund) =>
  hole<Ergebnis>(`/api/einreichungen/${id}/ablehnen`, {
    method: 'POST',
    body: JSON.stringify({ grundCode }),
  })

// --- Meldungen -----------------------------------------------------------

export type Folge =
  | 'keine'
  | 'bild_ablehnen'
  | 'bild_freigeben'
  | 'text_ablehnen'
  | 'bewertung_ablehnen'
  | 'ort_verbergen'
  | 'ort_archivieren'
  | 'dublette_zusammenlegen'

export interface Meldung {
  id: string
  kind: 'place' | 'media' | 'review'
  reason: string
  note: string | null
  created_at: string
  melder: string | null
  place_id: string | null
  media_id: string | null
  related_place_id: string | null
  reported_user_firebase_uid: string | null
  bild_pfad: string | null
  bild_status: string | null
  bild_hochlader: string | null
  ort_name: string | null
  ort_strasse: string | null
  ort_stadt: string | null
  ort_status: string | null
  ort_lat: number | null
  ort_lon: number | null
  zwilling_name: string | null
  zwilling_strasse: string | null
  zwilling_stadt: string | null
  bewertung_rating: number | null
  bewertung_titel: string | null
  bewertung_text: string | null
  bewertung_status: string | null
  bewertung_text_status: string | null
  folgen: Folge[]
  /** Die Meldung benennt ihr Ziel nicht — dann lässt sich nichts ausführen. */
  ziel_unvollstaendig: boolean
  mensch: Mensch | null
}

export const holeMeldungen = () => hole<Liste<Meldung>>('/api/meldungen')

/** Ein Ort mit dem, was an ihm hängt — für die Wahl beim Zusammenlegen. */
export interface Ortsstand {
  id: string
  name: string | null
  strasse: string | null
  plz: string | null
  stadt: string | null
  status: string
  herkunft: string | null
  angelegt: string
  fotos: number
  bewertungen: number
  merkmale: number
}

export interface Zusammenlegung {
  verlierer: Ortsstand
  sieger: Ortsstand
  vorschau: {
    fotos: number
    bewertungen: number
    doppelte_bewertungen: number
    neue_merkmale: string[]
  }
}

export const holeZusammenlegung = (verlierer: string, sieger: string) =>
  hole<Zusammenlegung>(
    `/api/zusammenlegen?verlierer=${encodeURIComponent(verlierer)}` +
      `&sieger=${encodeURIComponent(sieger)}`,
  )

/**
 * Zwei Orte zusammenlegen — ohne Meldung, aus der Dublettensuche heraus.
 * **Nicht umkehrbar**; die Oberfläche fragt vorher.
 */
export const zusammenlegen = (verlierer: string, sieger: string) =>
  hole<{ umzug: Record<string, unknown>; satz: string }>('/api/zusammenlegen', {
    method: 'POST',
    body: JSON.stringify({ verlierer, sieger }),
  })

export const meldungEntscheiden = (
  id: string,
  entscheidung: 'approved' | 'rejected',
  folge: Folge,
  siegerOrtId?: string | null,
  zwillingOrtId?: string | null,
) =>
  hole<Ergebnis & { folge: Folge; protokollzeilen: number }>(
    `/api/meldungen/${id}/entscheiden`,
    {
      method: 'POST',
      body: JSON.stringify({
        entscheidung,
        folge,
        siegerOrtId: siegerOrtId ?? null,
        zwillingOrtId: zwillingOrtId ?? null,
      }),
    },
  )

/**
 * Die Gründe, die eine Ablehnung dem **Menschen** mitteilt.
 *
 * Ein Code, kein Text: Die App baut daraus einen Satz in sechs Sprachen. Aus
 * „- kein Spielplatzbild" wird kein französischer Satz.
 *
 * Nicht zu verwechseln mit der Begründung daneben — die bleibt die interne
 * Notiz für die nächste Schicht und verlässt die Datenbank nie.
 */
export type Ablehnungsgrund =
  | 'duplicate'
  | 'permanently_closed'
  | 'outdated_photo'
  | 'wrong_data'
  | 'unsafe'
  | 'offensive'
  | 'persons_visible'
  | 'not_a_playground'
  | 'no_usable_text'
  | 'unusable_photo'
  | 'other'

export const ABLEHNUNGSGRUND_TEXT: Record<Ablehnungsgrund, string> = {
  not_a_playground: 'Kein Spielplatz zu sehen',
  unusable_photo: 'Bild unbrauchbar (unscharf, zu dunkel)',
  outdated_photo: 'Bild zeigt einen alten Zustand',
  persons_visible: 'Erkennbare Personen im Bild',
  no_usable_text: 'Kein brauchbarer Text',
  offensive: 'Anstößiger Inhalt',
  wrong_data: 'Angaben stimmen nicht',
  duplicate: 'Gibt es schon',
  permanently_closed: 'Existiert nicht mehr',
  unsafe: 'Sicherheitsproblem',
  other: 'Sonstiges',
}

/**
 * Welche Gründe je Warteschlange zur Wahl stehen — die wahrscheinlichsten
 * zuerst, `other` immer zuletzt.
 *
 * Die Trennung zweier Paare ist Absicht und keine Feinheit:
 *
 * `unusable_photo` statt `outdated_photo` bei einem verwackelten Bild — wer
 * „veraltet" hört, obwohl sein Bild unscharf ist, lädt dasselbe Bild noch
 * einmal hoch.
 *
 * `persons_visible` statt `offensive` bei erkennbaren Personen — das ist
 * Persönlichkeitsrecht, nicht Anstand.
 */
export const GRUENDE_FOTO: Ablehnungsgrund[] = [
  'not_a_playground',
  'unusable_photo',
  'outdated_photo',
  'persons_visible',
  'offensive',
  'duplicate',
  'other',
]

export const GRUENDE_TEXT: Ablehnungsgrund[] = [
  'no_usable_text',
  'offensive',
  'wrong_data',
  'other',
]

/**
 * An den drei Beitragsschlangen ist der Grund Pflicht.
 *
 * Der Stapel darf `null` durchreichen, weil die beiden Meldungsschlangen
 * keinen erheben — hier darf das nicht ankommen. Käme es doch an, wäre das
 * ein Fehler in dieser Datei und keine Lage, die der Server klären soll;
 * dann bricht es hier ab, mit einem Satz, statt dort mit einer 23514.
 */
export function pflichtGrund(code: Ablehnungsgrund | null): Ablehnungsgrund {
  if (!code) {
    throw new Error(
      'Ohne Grund lässt sich hier nichts ablehnen — der Dialog hätte einen erheben müssen.',
    )
  }
  return code
}

/**
 * Beim Profilbild: `persons_visible` meint hier nicht „ein Mensch ist zu
 * sehen" — das ist der Zweck eines Profilbilds —, sondern **fremde**
 * Erkennbare, meist ein Kind. Deshalb steht der Grund oben und nicht als
 * Sonderfall von `offensive`.
 *
 * `not_a_playground` fehlt: Ein Profilbild soll keinen Spielplatz zeigen.
 */
export const GRUENDE_PROFILBILD: Ablehnungsgrund[] = [
  'offensive',
  'persons_visible',
  'unusable_photo',
  'other',
]

export const GRUENDE_EINREICHUNG: Ablehnungsgrund[] = [
  'not_a_playground',
  'duplicate',
  'wrong_data',
  'permanently_closed',
  'unsafe',
  'offensive',
  'other',
]

/** Die Gründe einer Meldung im Klartext (§8.5). */
export const GRUND_TEXT: Record<string, string> = {
  duplicate: 'Dublette',
  permanently_closed: 'dauerhaft geschlossen',
  outdated_photo: 'Foto veraltet',
  wrong_data: 'falsche Angaben',
  unsafe: 'unsicher',
  offensive: 'anstößig',
  persons_visible: 'Personen erkennbar',
  other: 'sonstiges',
}

export const FOLGE_TEXT: Record<Folge, string> = {
  keine: 'nur entscheiden, nichts ausführen',
  bild_ablehnen: 'Bild endgültig ablehnen',
  bild_freigeben: 'Bild wieder freigeben',
  text_ablehnen: 'Text ablehnen (Sterne bleiben)',
  bewertung_ablehnen: 'ganze Bewertung ablehnen (Sterne zählen nicht mehr)',
  ort_verbergen: 'Ort verbergen',
  ort_archivieren: 'Ort archivieren',
  dublette_zusammenlegen: 'Dublette zusammenlegen',
}

/**
 * Was die Folge bewirkt — in der Sprache dessen, der entscheidet.
 *
 * Hier standen einmal die Spaltennamen. Das war Dokumentation, kein Werkzeug:
 * Wer moderiert, will wissen, was mit dem Beitrag geschieht, nicht welches
 * Feld dabei welchen Wert bekommt. Das steht im Code, wo es hingehört.
 */
export const FOLGE_WIRKUNG: Record<Folge, string> = {
  keine: 'Am Beitrag ändert sich nichts. Nur die Meldung wird entschieden.',
  bild_ablehnen:
    'Das Bild verschwindet aus der App, die 3 Punkte gehen an den Hochlader ' +
    'zurück. Die Datei selbst bleibt liegen.',
  bild_freigeben:
    'Das Bild ist wieder sichtbar, die Punkte bleiben beim Hochlader.',
  text_ablehnen:
    'Titel und Text verschwinden, die Sterne zählen weiter im Durchschnitt. ' +
    'Von 6 Punkten bleiben 2.',
  bewertung_ablehnen:
    'Die ganze Bewertung zählt nicht mehr, auch die Sterne nicht. Alle Punkte ' +
    'gehen zurück.',
  ort_verbergen:
    'In der App nicht mehr zu finden, bleibt aber erhalten und lässt sich ' +
    'zurückholen. Für Fälle, in denen die Entscheidung noch offen ist.',
  ort_archivieren:
    'Endgültig weggeräumt — für Orte, die es nicht mehr gibt. Ein dauerhaft ' +
    'geschlossener Spielplatz gehört hierher.',
  dublette_zusammenlegen:
    'Der gemeldete Ort geht im gewählten auf: Wer ihn aufruft, landet künftig ' +
    'beim bleibenden. Gelöscht wird nichts.',
}

// --- Meldungen an Profilen (core_report.kind = 'user') -------------------

/**
 * Eine Profilmeldung zeigt auf **nichts außer einer Person**: `place_id` und
 * `media_id` sind beide leer. Deshalb eine eigene Warteschlange — in der
 * Meldungsansicht wäre sie eine leere Karte.
 */
export interface Profilmeldung {
  id: string
  created_at: string
  reason: string
  note: string | null
  state: string

  gemeldet_uid: string
  gemeldet_name: string | null
  gemeldet_text: string | null
  /** Auch im verborgenen Zustand lesbar — sonst kann man nicht entscheiden. */
  gemeldet_text_roh: string | null
  textstatus: string
  gemeldet_punkte: number
  profile_hidden: boolean
  gemeldet_bild: string | null
  gemeldet_bild_klein: string | null
  gemeldet_bild_id: string | null
  gemeldet_bild_status: string | null
  gemeldet_seit: string
  zuletzt_gesehen: string | null

  melder: string
  meldungen_gesamt: number
  abgelehnte_beitraege: number
}

export const holeProfilmeldungen = () =>
  hole<Liste<Profilmeldung>>('/api/profilmeldungen')

export type Sichtbarkeit = 'published' | 'hidden'

export const selbstbeschreibungSetzen = (uid: string, status: Sichtbarkeit) =>
  hole<{ uid: string; textstatus: Sichtbarkeit }>(
    `/api/profilmeldungen/text/${encodeURIComponent(uid)}`,
    { method: 'POST', body: JSON.stringify({ status }) },
  )

/**
 * Beim Verbergen ist ein Grund Pflicht — anders als beim Text entsteht hier
 * eine Zeile in `core_decision`, und die geht den Menschen etwas an. Beim
 * Wiederzeigen wird keiner mitgeschickt.
 */
export const profilbildSetzen = (
  mediaId: string,
  status: Sichtbarkeit,
  grundCode?: Ablehnungsgrund | null,
) =>
  hole<{ id: string; status: Sichtbarkeit; protokolliert: boolean }>(
    `/api/profilmeldungen/bild/${encodeURIComponent(mediaId)}`,
    {
      method: 'POST',
      body: JSON.stringify({
        status,
        grundCode: status === 'hidden' ? (grundCode ?? null) : null,
      }),
    },
  )

export const profilmeldungEntscheiden = (
  id: string,
  entscheidung: 'approved' | 'rejected',
) =>
  hole<Ergebnis & { state: string }>(`/api/profilmeldungen/${id}/entscheiden`, {
    method: 'POST',
    body: JSON.stringify({ entscheidung }),
  })
